/**
 * API Client for Email Logs
 * Handles HTTP requests for managing email logs and statistics
 */
import api from './api';

export type EmailStatus = 'queued' | 'sent' | 'failed' | 'bounced' | 'delivered' | 'opened' | 'clicked';

export interface EmailLog {
  id: string;
  tenantId: string;
  companyId: string;
  to: string;
  subject: string;
  bodyHtml?: string;
  status: EmailStatus;
  entityType?: string;
  entityId?: string;
  providerMessageId?: string;
  sentAt?: string;
  deliveredAt?: string;
  openedAt?: string;
  clickedAt?: string;
  errorMessage?: string;
  createdAt: string;
  userId?: string;
  Company?: {
    id: string;
    name: string;
  };
  User?: {
    id: string;
    email: string;
    fullName: string;
  };
}

export interface EmailStatistics {
  total: number;
  sent: number;
  failed: number;
  bounced: number;
  queued: number;
}

export interface GetEmailLogsParams {
  status?: EmailStatus;
  relatedEntityType?: string;
  limit?: number;
  offset?: number;
}

export interface EmailLogsResponse {
  success: boolean;
  data: EmailLog[];
  count: number;
  message?: string;
}

export interface EmailStatisticsResponse {
  success: boolean;
  data: EmailStatistics;
  message?: string;
}

export interface EmailHistoryResponse {
  success: boolean;
  data: EmailLog[];
  count: number;
  message?: string;
}

/**
 * API client for Email Logs
 */
export const emailLogsAPI = {
  /**
   * Get all email logs with optional filters
   */
  async getLogs(params?: GetEmailLogsParams): Promise<{ logs: EmailLog[]; count: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.relatedEntityType) queryParams.append('relatedEntityType', params.relatedEntityType);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const response = await api.get<EmailLogsResponse>(
      `/emails/logs?${queryParams.toString()}`
    );
    return {
      logs: response.data,
      count: (response as any).count || 0,
    };
  },

  /**
   * Get email statistics
   */
  async getStatistics(startDate?: string, endDate?: string): Promise<EmailStatistics> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await api.get<EmailStatisticsResponse>(
      `/emails/statistics?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Get email history for a specific entity
   */
  async getHistory(entityType: string, entityId: string): Promise<EmailLog[]> {
    const response = await api.get<EmailHistoryResponse>(
      `/emails/history/${entityType}/${entityId}`
    );
    return response.data;
  },

  /**
   * Send a test email
   */
  async sendTestEmail(): Promise<{ success: boolean; messageId: string; message: string }> {
    const response = await api.post<{ success: boolean; messageId: string; message: string }>(
      '/emails/test',
      {}
    );
    // Response is returned directly, not wrapped
    return response as any;
  },

  /**
   * Get email service status
   */
  async getStatus(): Promise<{ provider: string; configured: boolean }> {
    const response = await api.get<{ success: boolean; data: { provider: string; configured: boolean } }>(
      '/emails/status'
    );
    return response.data;
  },
};
